import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import translations from '../i18n/translations';
import { setDocumentLang } from '../utils/seo';
import { trackEvent, Events } from '../utils/webviewBridge';

const LocaleContext = createContext(null);

const STORAGE_KEY = 'vfootball-locale';
const EXPLICIT_KEY = 'vfootball-locale-explicit';

function detectBrowserLocale() {
  const candidates = [
    ...(typeof navigator !== 'undefined' && Array.isArray(navigator.languages)
      ? navigator.languages
      : []),
    typeof navigator !== 'undefined' ? navigator.language : '',
    typeof navigator !== 'undefined' ? navigator.userLanguage : ''
  ].filter(Boolean);

  for (const raw of candidates) {
    const code = String(raw).toLowerCase().split(/[-_]/)[0];
    if (code === 'pt') return 'pt';
    if (code === 'en') return 'en';
  }

  return 'pt';
}

function readInitialLocale() {
  try {
    if (localStorage.getItem(EXPLICIT_KEY) === '1') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'pt' || saved === 'en') return saved;
    }
  } catch {
    /* private mode */
  }
  return detectBrowserLocale();
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(readInitialLocale);

  useEffect(() => {
    setDocumentLang(locale);
  }, [locale]);

  const setLocale = (next) => {
    if (next !== 'pt' && next !== 'en') return;
    if (next !== locale) {
      trackEvent(Events.LOCALE_CHANGED, { locale: next });
    }
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
      localStorage.setItem(EXPLICIT_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const t = useMemo(() => translations[locale] || translations.en, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      isPt: locale === 'pt'
    }),
    [locale, t]
  );

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;
